/**
 * Text Animator - 完整的文字动画解决方案
 * 结合原生 JS 文字打散 + GSAP 动画引擎
 * 适用于 Shopify 及其他项目
 *
 * @version 1.0.0
 * @author Your Name
 * @requires GSAP 3.x
 * @requires ScrollTrigger (optional)
 */

(function (window, document) {
  "use strict";

  /**
   * 主要的文字动画类
   */
  class TextAnimator {
    constructor(options = {}) {
      // 默认配置
      this.config = {
        // 基础设置
        autoInit: true,
        debug: false,

        // 动画默认参数
        defaultDuration: 0.6,
        defaultStagger: 0.05,
        defaultEase: "none", // 默认使用匀速动画
        defaultDelay: 0,

        // 分割设置
        splitBy: "chars", // 'chars', 'words', 'lines'
        preserveSpaces: true,

        // 选择器
        selector: "[data-text-animate]",
        triggerSelector: '[data-animate-trigger="scroll"]',

        // ScrollTrigger 设置
        scrollTriggerStart: "top 80%",
        scrollTriggerEnd: "bottom 20%",

        ...options,
      };

      // 实例管理
      this.instances = new Map();
      this.animations = new Set();
      this.isGSAPReady = false;
      this.isScrollTriggerReady = false;

      // 性能监控
      this.performanceData = {
        animationCount: 0,
        splitCount: 0,
      };

      // 自动初始化
      if (this.config.autoInit) {
        this.init();
      }
    }

    /**
     * 初始化动画器
     */
    async init() {
      try {
        await this.waitForGSAP();
        this.log("GSAP 已就绪");

        await this.checkScrollTrigger();

        this.setupEventListeners();
        this.scanAndAnimate();

        this.log("TextAnimator 初始化完成");
      } catch (error) {
        console.error("TextAnimator 初始化失败:", error);
      }
    }

    /**
     * 等待 GSAP 加载
     */
    waitForGSAP() {
      return new Promise((resolve, reject) => {
        if (typeof gsap !== "undefined") {
          this.isGSAPReady = true;
          resolve();
          return;
        }

        let attempts = 0;
        const maxAttempts = 100; // 10秒超时

        const checkInterval = setInterval(() => {
          attempts++;

          if (typeof gsap !== "undefined") {
            clearInterval(checkInterval);
            this.isGSAPReady = true;
            resolve();
          } else if (attempts >= maxAttempts) {
            clearInterval(checkInterval);
            reject(new Error("GSAP 加载超时"));
          }
        }, 100);
      });
    }

    /**
     * 检查 ScrollTrigger 插件
     */
    async checkScrollTrigger() {
      if (typeof ScrollTrigger !== "undefined") {
        gsap.registerPlugin(ScrollTrigger);
        this.isScrollTriggerReady = true;
        this.log("ScrollTrigger 已就绪");
      } else {
        this.log("ScrollTrigger 未加载，滚动触发功能不可用");
      }
    }

    /**
     * 设置事件监听器
     */
    setupEventListeners() {
      // DOM 加载完成
      if (document.readyState === "loading") {
        document.addEventListener("DOMContentLoaded", () => {
          this.scanAndAnimate();
        });
      }

      // 页面显示（处理浏览器返回等情况）
      window.addEventListener("pageshow", (event) => {
        if (event.persisted) {
          this.refreshAnimations();
        }
      });

      // Shopify 主题编辑器支持
      if (typeof Shopify !== "undefined" && Shopify.designMode) {
        document.addEventListener("shopify:section:load", (event) => {
          this.animateSection(event.target);
        });

        document.addEventListener("shopify:section:unload", (event) => {
          this.cleanupSection(event.target);
        });
      }

      // 窗口调整大小时刷新 ScrollTrigger
      if (this.isScrollTriggerReady) {
        window.addEventListener(
          "resize",
          this.debounce(() => {
            ScrollTrigger.refresh();
          }, 250)
        );
      }

      // 页面卸载时清理
      window.addEventListener("beforeunload", () => {
        this.destroy();
      });
    }

    /**
     * 扫描页面并执行动画
     */
    scanAndAnimate() {
      if (!this.isGSAPReady) {
        this.log("GSAP 未就绪，跳过扫描");
        return;
      }

      const elements = document.querySelectorAll(this.config.selector);
      this.log(`找到 ${elements.length} 个待动画元素`);

      elements.forEach((element, index) => {
        // 避免重复处理
        if (element.hasAttribute("data-animated")) {
          return;
        }

        const animationType =
          element.getAttribute("data-text-animate") || "slide";
        const trigger = element.getAttribute("data-animate-trigger") || "load";
        const delay = this.parseFloat(
          element.getAttribute("data-animate-delay"),
          index * 0.3
        );

        if (trigger === "load") {
          setTimeout(() => {
            this.animateText(element, animationType, { delay: 0 });
          }, delay * 1000);
        } else if (trigger === "scroll" && this.isScrollTriggerReady) {
          this.setupScrollAnimation(element, animationType);
        }

        element.setAttribute("data-animated", "true");
      });
    }

    /**
     * 设置滚动触发动画
     */
    setupScrollAnimation(element, animationType) {
      ScrollTrigger.create({
        trigger: element,
        start: this.config.scrollTriggerStart,
        end: this.config.scrollTriggerEnd,
        onEnter: () => {
          this.animateText(element, animationType);
        },
        once: true,
        invalidateOnRefresh: true,
      });
    }

    /**
     * 文字分割核心方法
     */
    splitText(element, options = {}) {
      const config = {
        splitBy: options.splitBy || this.config.splitBy,
        className: options.className || "char",
        preserveSpaces:
          options.preserveSpaces !== undefined
            ? options.preserveSpaces
            : this.config.preserveSpaces,
        ...options,
      };

      // 获取原始文本
      const originalText =
        element.getAttribute("data-text") ||
        element.textContent.trim() ||
        element.innerText.trim();

      if (!originalText) {
        this.log("警告: 元素没有文本内容", element);
        return { instanceId: null, elements: [], revert: () => {} };
      }

      const instanceId = this.generateId();

      // 保存实例信息
      this.instances.set(instanceId, {
        element,
        originalText,
        config: { ...config },
      });

      // 清空元素
      element.innerHTML = "";
      element.setAttribute("data-split-id", instanceId);

      let splitElements = [];

      try {
        switch (config.splitBy) {
          case "chars":
            splitElements = this.splitToChars(originalText, config, element);
            break;
          case "words":
            splitElements = this.splitToWords(originalText, config, element);
            break;
          case "lines":
            splitElements = this.splitToLines(originalText, config, element);
            break;
          default:
            throw new Error(`不支持的分割类型: ${config.splitBy}`);
        }

        this.performanceData.splitCount++;
        this.log(`文字分割完成: ${splitElements.length} 个元素`);
      } catch (error) {
        console.error("文字分割失败:", error);
        element.textContent = originalText;
        return { instanceId: null, elements: [], revert: () => {} };
      }

      return {
        instanceId,
        elements: splitElements,
        revert: () => this.revertSplit(instanceId),
      };
    }

    /**
     * 按字符分割
     */
    splitToChars(text, config, parent) {
      const chars = text.split("");
      const elements = [];

      chars.forEach((char, index) => {
        const span = document.createElement("span");
        span.className = config.className;
        span.style.display = "inline-block";
        span.style.willChange = "transform, opacity";
        span.setAttribute("data-char-index", index);

        if (char === " ") {
          if (config.preserveSpaces) {
            span.innerHTML = "&nbsp;";
            span.style.width = "0.25em";
          } else {
            span.style.width = "0.25em";
            span.innerHTML = "&nbsp;";
          }
        } else if (char === "\n") {
          span.style.display = "block";
          span.style.height = "1em";
        } else {
          span.textContent = char;
        }

        parent.appendChild(span);
        elements.push(span);
      });

      return elements;
    }

    /**
     * 按单词分割
     */
    splitToWords(text, config, parent) {
      const words = text.trim().split(/(\s+)/);
      const elements = [];

      words.forEach((word, index) => {
        if (word.trim()) {
          // 非空格内容
          const span = document.createElement("span");
          span.className = config.className;
          span.style.display = "inline-block";
          span.style.willChange = "transform, opacity";
          span.textContent = word;
          span.setAttribute("data-word-index", index);

          parent.appendChild(span);
          elements.push(span);
        } else if (word && config.preserveSpaces) {
          // 空格
          const space = document.createElement("span");
          space.innerHTML = "&nbsp;";
          space.style.display = "inline-block";
          parent.appendChild(space);
        }
      });

      return elements;
    }

    /**
     * 按行分割
     */
    splitToLines(text, config, parent) {
      const lines = text.split("\n").filter((line) => line.trim());
      const elements = [];

      lines.forEach((line, index) => {
        const div = document.createElement("div");
        div.className = config.className;
        div.style.display = "block";
        div.style.willChange = "transform, opacity";
        div.textContent = line.trim();
        div.setAttribute("data-line-index", index);

        parent.appendChild(div);
        elements.push(div);
      });

      return elements;
    }

    /**
     * 执行文字动画
     */
    animateText(element, animationType = "slide", options = {}) {
      if (!this.isGSAPReady) {
        this.log("GSAP 未就绪，跳过动画");
        return null;
      }

      // 解析选项
      const animationOptions = {
        duration: this.parseFloat(
          element.getAttribute("data-animate-duration"),
          options.duration || this.config.defaultDuration
        ),
        stagger: this.parseFloat(
          element.getAttribute("data-animate-stagger"),
          options.stagger || this.config.defaultStagger
        ),
        delay: this.parseFloat(
          element.getAttribute("data-animate-delay"),
          options.delay || this.config.defaultDelay
        ),
        ease:
          element.getAttribute("data-animate-ease") ||
          options.ease ||
          this.config.defaultEase,
        splitBy:
          element.getAttribute("data-split-by") ||
          options.splitBy ||
          this.config.splitBy,
        ...options,
      };

      // 分割文字
      const splitResult = this.splitText(element, {
        splitBy: animationOptions.splitBy,
      });

      if (!splitResult.elements.length) {
        this.log("没有可动画的元素");
        return null;
      }

      // 分割文字后，确保父元素是可见的
      gsap.set(element, { opacity: 1, y: 0 });

      // 创建动画
      let animation;
      try {
        animation = this.createAnimation(
          animationType,
          splitResult.elements,
          animationOptions
        );

        if (animation) {
          this.animations.add(animation);
          this.performanceData.animationCount++;

          // 动画完成后清理
          animation
            .then(() => {
              this.animations.delete(animation);
              this.performanceData.animationCount--;
            })
            .catch((error) => {
              console.error("动画执行失败:", error);
              this.animations.delete(animation);
              this.performanceData.animationCount--;
            });
        }

        this.log(`动画创建成功: ${animationType}`);
      } catch (error) {
        console.error("动画创建失败:", error);
        splitResult.revert();
        return null;
      }

      return {
        animation,
        splitResult,
        kill: () => {
          if (animation && animation.kill) {
            animation.kill();
            this.animations.delete(animation);
            this.performanceData.animationCount--;
          }
          splitResult.revert();
        },
      };
    }

    /**
     * 创建动画
     */
    createAnimation(type, elements, options) {
      const animations = {
        fade: () => this.fadeAnimation(elements, options),
        slide: () => this.slideAnimation(elements, options),
        slideUp: () => this.slideUpAnimation(elements, options),
        slideLeft: () => this.slideLeftAnimation(elements, options),
        slideRight: () => this.slideRightAnimation(elements, options),
        bounce: () => this.bounceAnimation(elements, options),
        wave: () => this.waveAnimation(elements, options),
        rotate: () => this.rotateAnimation(elements, options),
        rotateX: () => this.rotateXAnimation(elements, options),
        rotateY: () => this.rotateYAnimation(elements, options),
        scale: () => this.scaleAnimation(elements, options),
        typewriter: () => this.typewriterAnimation(elements, options),
        blur: () => this.blurAnimation(elements, options),
        elastic: () => this.elasticAnimation(elements, options),
        slideSmooth: () => this.slideSmoothAnimation(elements, options),
      };

      const animationFunction = animations[type];
      if (!animationFunction) {
        throw new Error(`不支持的动画类型: ${type}`);
      }

      return animationFunction();
    }

    /**
     * 动画效果定义
     */
    fadeAnimation(elements, options) {
      gsap.set(elements, { opacity: 0 });
      return gsap.to(elements, {
        opacity: 1,
        duration: options.duration,
        stagger: options.stagger,
        delay: options.delay,
        ease: options.ease,
      });
    }

    slideAnimation(elements, options) {
      gsap.set(elements, { y: "100%", opacity: 0 });
      return gsap.to(elements, {
        y: "0%",
        opacity: 1,
        duration: options.duration,
        stagger: options.stagger,
        delay: options.delay,
        ease: options.ease,
      });
    }

    slideUpAnimation(elements, options) {
      gsap.set(elements, { y: "-100%", opacity: 0 });
      return gsap.to(elements, {
        y: "0%",
        opacity: 1,
        duration: options.duration,
        stagger: options.stagger,
        delay: options.delay,
        ease: options.ease,
      });
    }

    slideLeftAnimation(elements, options) {
      gsap.set(elements, { x: "100%", opacity: 0 });
      return gsap.to(elements, {
        x: "0%",
        opacity: 1,
        duration: options.duration,
        stagger: options.stagger,
        delay: options.delay,
        ease: options.ease,
      });
    }

    slideRightAnimation(elements, options) {
      gsap.set(elements, { x: "-100%", opacity: 0 });
      return gsap.to(elements, {
        x: "0%",
        opacity: 1,
        duration: options.duration,
        stagger: options.stagger,
        delay: options.delay,
        ease: options.ease,
      });
    }

    bounceAnimation(elements, options) {
      gsap.set(elements, { y: -50, opacity: 0, scale: 0.8 });
      return gsap.to(elements, {
        y: 0,
        opacity: 1,
        scale: 1,
        duration: options.duration,
        stagger: options.stagger,
        delay: options.delay,
        ease: "bounce.out",
      });
    }

    waveAnimation(elements, options) {
      gsap.set(elements, { y: 30, opacity: 0 });
      return gsap.to(elements, {
        y: 0,
        opacity: 1,
        duration: options.duration,
        stagger: {
          amount: options.stagger * elements.length,
          from: "start",
          ease: "sine.inOut",
        },
        delay: options.delay,
        ease: "elastic.out(1, 0.5)",
      });
    }

    rotateAnimation(elements, options) {
      gsap.set(elements, { rotation: 180, opacity: 0, scale: 0.5 });
      return gsap.to(elements, {
        rotation: 0,
        opacity: 1,
        scale: 1,
        duration: options.duration,
        stagger: options.stagger,
        delay: options.delay,
        ease: options.ease,
      });
    }

    rotateXAnimation(elements, options) {
      gsap.set(elements, {
        rotationX: 90,
        opacity: 0,
        transformOrigin: "center center",
        transformPerspective: 1000,
      });
      return gsap.to(elements, {
        rotationX: 0,
        opacity: 1,
        duration: options.duration,
        stagger: options.stagger,
        delay: options.delay,
        ease: options.ease,
      });
    }

    rotateYAnimation(elements, options) {
      gsap.set(elements, {
        rotationY: 90,
        opacity: 0,
        transformOrigin: "center center",
        transformPerspective: 1000,
      });
      return gsap.to(elements, {
        rotationY: 0,
        opacity: 1,
        duration: options.duration,
        stagger: options.stagger,
        delay: options.delay,
        ease: options.ease,
      });
    }

    scaleAnimation(elements, options) {
      gsap.set(elements, { scale: 0, opacity: 0 });
      return gsap.to(elements, {
        scale: 1,
        opacity: 1,
        duration: options.duration,
        stagger: options.stagger,
        delay: options.delay,
        ease: "elastic.out(1, 0.75)",
      });
    }

    typewriterAnimation(elements, options) {
      gsap.set(elements, { opacity: 0 });
      return gsap.to(elements, {
        opacity: 1,
        duration: 0.05,
        stagger: options.stagger || 0.1,
        delay: options.delay,
        ease: "none",
      });
    }

    blurAnimation(elements, options) {
      gsap.set(elements, { filter: "blur(10px)", opacity: 0 });
      return gsap.to(elements, {
        filter: "blur(0px)",
        opacity: 1,
        duration: options.duration,
        stagger: options.stagger,
        delay: options.delay,
        ease: options.ease,
      });
    }

    elasticAnimation(elements, options) {
      gsap.set(elements, { scaleX: 0, opacity: 0 });
      return gsap.to(elements, {
        scaleX: 1,
        opacity: 1,
        duration: options.duration,
        stagger: options.stagger,
        delay: options.delay,
        ease: "elastic.out(1, 0.8)",
      });
    }

    slideSmoothAnimation(elements, options) {
      // 设置子元素（字符）的初始状态
      gsap.set(elements, { y: 30, opacity: 0 });

      // 设置父元素为可见（如果存在的话）
      if (elements.length > 0) {
        const parentElement = elements[0].parentElement;
        if (parentElement && parentElement.hasAttribute("data-text-animate")) {
          gsap.set(parentElement, { opacity: 1, y: 0 });
        }
      }

      return gsap.to(elements, {
        y: 0,
        opacity: 1,
        duration: options.duration,
        stagger: options.stagger,
        delay: options.delay,
        ease: "none", // 匀速动画
      });
    }

    /**
     * 为特定区域设置动画
     */
    animateSection(section) {
      const elements = section.querySelectorAll(this.config.selector);

      elements.forEach((element, index) => {
        // 移除已动画标记，允许重新动画
        element.removeAttribute("data-animated");

        const animationType =
          element.getAttribute("data-text-animate") || "slide";
        const delay = index * 0.2;

        setTimeout(() => {
          this.animateText(element, animationType);
        }, delay * 1000);
      });
    }

    /**
     * 清理特定区域
     */
    cleanupSection(section) {
      const elements = section.querySelectorAll("[data-split-id]");

      elements.forEach((element) => {
        const splitId = element.getAttribute("data-split-id");
        if (splitId) {
          this.revertSplit(splitId);
        }
      });
    }

    /**
     * 刷新所有动画
     */
    refreshAnimations() {
      // 清理现有实例
      this.instances.forEach((instance, id) => {
        this.revertSplit(id);
      });

      // 移除所有动画标记
      document.querySelectorAll("[data-animated]").forEach((element) => {
        element.removeAttribute("data-animated");
      });

      // 重新扫描和动画
      setTimeout(() => {
        this.scanAndAnimate();

        if (this.isScrollTriggerReady) {
          ScrollTrigger.refresh();
        }
      }, 100);
    }

    /**
     * 恢复文字分割
     */
    revertSplit(instanceId) {
      const instance = this.instances.get(instanceId);
      if (instance) {
        instance.element.textContent = instance.originalText;
        instance.element.removeAttribute("data-split-id");
        this.instances.delete(instanceId);
        this.performanceData.splitCount--;
      }
    }

    /**
     * 清理所有动画和实例
     */
    destroy() {
      // 停止所有动画
      this.animations.forEach((animation) => {
        if (animation && animation.kill) {
          animation.kill();
        }
      });
      this.animations.clear();

      // 恢复所有文字分割
      this.instances.forEach((instance, id) => {
        this.revertSplit(id);
      });

      // 清理 ScrollTrigger
      if (this.isScrollTriggerReady) {
        ScrollTrigger.getAll().forEach((trigger) => trigger.kill());
      }

      // 重置性能数据
      this.performanceData = {
        animationCount: 0,
        splitCount: 0,
      };

      this.log("TextAnimator 已销毁");
    }

    /**
     * 工具方法
     */
    parseFloat(value, defaultValue) {
      const parsed = parseFloat(value);
      return !isNaN(parsed) ? parsed : defaultValue;
    }

    generateId() {
      return Date.now().toString(36) + Math.random().toString(36).substr(2);
    }

    debounce(func, wait) {
      let timeout;
      return function executedFunction(...args) {
        const later = () => {
          clearTimeout(timeout);
          func(...args);
        };
        clearTimeout(timeout);
        timeout = setTimeout(later, wait);
      };
    }

    log(message, ...args) {
      if (this.config.debug) {
        console.log(`[TextAnimator] ${message}`, ...args);
      }
    }

    /**
     * 获取性能数据
     */
    getPerformanceData() {
      return { ...this.performanceData };
    }

    /**
     * 公共 API 方法
     */

    // 手动触发动画
    trigger(selector, animationType = "slide", options = {}) {
      const elements = document.querySelectorAll(selector);
      const results = [];

      elements.forEach((element) => {
        element.removeAttribute("data-animated");
        const result = this.animateText(element, animationType, options);
        if (result) {
          results.push(result);
        }
      });

      return results;
    }

    // 停止动画
    stop(selector) {
      const elements = document.querySelectorAll(selector);

      elements.forEach((element) => {
        const splitId = element.getAttribute("data-split-id");
        if (splitId) {
          this.revertSplit(splitId);
        }
      });
    }

    // 重新开始动画
    restart(selector) {
      this.stop(selector);
      setTimeout(() => {
        this.trigger(selector);
      }, 100);
    }
  }

  // 导出到全局
  window.TextAnimator = TextAnimator;

  // 自动创建默认实例
  let defaultInstance = null;

  function initDefaultTextAnimator() {
    if (typeof gsap !== "undefined" && !defaultInstance) {
      defaultInstance = new TextAnimator({
        debug: false, // 在开发时设置为 true
      });

      window.textAnimator = defaultInstance;

      // 在控制台提供帮助信息
      if (defaultInstance.config.debug) {
        console.log(
          "%c🎨 TextAnimator 已初始化",
          "color: #4CAF50; font-weight: bold;"
        );
        console.log("使用 window.textAnimator 访问实例");
        console.log("可用的动画类型:", [
          "fade",
          "slide",
          "slideUp",
          "slideLeft",
          "slideRight",
          "bounce",
          "wave",
          "rotate",
          "rotateX",
          "rotateY",
          "scale",
          "typewriter",
          "blur",
          "elastic",
          "slideSmooth",
        ]);
      }
    }
  }

  // 自动初始化
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initDefaultTextAnimator);
  } else {
    initDefaultTextAnimator();
  }

  // 如果 GSAP 还没加载，等待加载
  if (typeof gsap === "undefined") {
    const checkGSAP = () => {
      if (typeof gsap !== "undefined") {
        initDefaultTextAnimator();
      } else {
        setTimeout(checkGSAP, 100);
      }
    };
    checkGSAP();
  }
})(window, document);
